import { GzLandingPageWebComponent } from "../../../common/gzLandingPage";
import { property } from 'lit/decorators.js';
import {
  MspInProductTrialCannotBeStartedReason,
  MspIptButtonItems
} from "../../../../company/common/mspInProductTrialsTab/types/msp-in-product-trials-types";
import { html, TemplateResult } from "lit";
import { LandingPageEventsName } from "../../../common/gzLandingPage/types";
import { CountdownTranslations } from "../../../../ui/gzCountDown/types";
import { classMap } from 'lit/directives/class-map.js';
import { customElement } from 'lit/decorators.js';

@customElement('gz-msp-landing-page')
export class MspLandingPageWebComponent extends GzLandingPageWebComponent {
  @property({
    type: Array,
    attribute: false,
    reflect: false,
  })
  buttonItems: MspIptButtonItems;
  @property({
    type: Boolean,
    attribute: false,
    reflect: false,
  })
  hasBackButton = false;
  @property({
    type: Boolean,
    attribute: false,
    reflect: false,
  })
  isMsp = false;

  @property({
    type: Boolean,
    attribute: false,
    reflect: false,
  })
  isMdrOnboarding = false;

  @property({type: String})
  mdrOnboardingMessage = '';

  @property({type: String})
  mdrOnboardingTooltip = '';

  public setMdrOnboarding(isMdrOnboarding: boolean, mdrOnboardingMessage: string = '', mdrOnboardingTooltip: string = ''): void {
    this.isMdrOnboarding = isMdrOnboarding;
    this.mdrOnboardingMessage = mdrOnboardingMessage;
    this.mdrOnboardingTooltip = mdrOnboardingTooltip;
  }

  public setCannotBeStartedReason(cannotBeStartedReason?: MspInProductTrialCannotBeStartedReason): void {
    this.cannotBeStartedReason = cannotBeStartedReason;
  }

  protected renderButtons(): TemplateResult | undefined {
    if (!this.buttonItems || Object.keys(this.buttonItems).length === 0) {
      return;
    }

    return html`
      <div
        class="card-section pt-6 px-4 layout-3-columns msp-trial-action-buttons-wrapper">
        <div class="action-button ${this.buttonItems.reconfigureTaskButton ? '' : 'hidden'}">
          <div class="action-button-description">
            <gz-title class="action-button-title">
              ${this.buttonItems.reconfigureTaskButton?.buttonTitle}
            </gz-title>
            <gz-text class="action-button-text">
              ${this.buttonItems.reconfigureTaskButton?.buttonDescription}
            </gz-text>
          </div>
          <gz-button class="action-button-submit"
                     id="${this.buttonItems.reconfigureTaskButton?.buttonId}"
                     @click="${this.handleButtonClicked}"
          >${this.buttonItems.reconfigureTaskButton?.buttonText}
          </gz-button>
        </div>

        <div class="action-button ${this.buttonItems.generateReportButton ? '' : 'hidden'}">
          <div class="action-button-description">
            <gz-title class="action-button-title">
              ${this.buttonItems.generateReportButton?.buttonTitle}
            </gz-title>
            <gz-text class="action-button-text">
              ${this.buttonItems.generateReportButton?.buttonDescription}
            </gz-text>
          </div>
          <gz-button class="action-button-submit"
                     id="${this.buttonItems.generateReportButton?.buttonId}"
                     @click="${this.handleButtonClicked}"
          >${this.buttonItems.generateReportButton?.buttonText}
          </gz-button>
        </div>
      </div>
    `;
  }

  protected renderCountDown(translations?: CountdownTranslations): TemplateResult | undefined {
    const isCountDownAllowedForReason = this.cannotBeStartedReason === MspInProductTrialCannotBeStartedReason.TrialAlreadyInProgressForCurrentProduct ||
      this.cannotBeStartedReason === MspInProductTrialCannotBeStartedReason.InCooldown;
    if (!isCountDownAllowedForReason || (!this.dateToTimestamp && !this.isMdrOnboarding)) {
      return;
    }
    const component = this.createCountDownWebComponent(translations);
    if (component) {
      component.inStandBy = this.isMdrOnboarding;
      component.standByTranslation = this.mdrOnboardingMessage;
      component.standByTooltip = this.mdrOnboardingTooltip;
    }

    return html`${component}`;
  }

  protected renderProduct() {
    if (!this.productItem) {
      return;
    }

    const classes = classMap({
      'is-start-available':
        this.cannotBeStartedReason !== MspInProductTrialCannotBeStartedReason.TrialAlreadyInProgressForCurrentProduct,
      'is-stop-available':
        this.cannotBeStartedReason === MspInProductTrialCannotBeStartedReason.TrialAlreadyInProgressForCurrentProduct,
      'is-demo-available': !!this.productItem.isDemoAvailable,
      'is-how-to-available': !!this.productItem.isHowToAvailable,
    });

    const tooltipText = this.cannotBeStartedReason
      ? this.productItem.tooltipTextMapping[this.cannotBeStartedReason]
      : '';

    const startButtonMustBeDisabled = this.cannotBeStartedReason !== MspInProductTrialCannotBeStartedReason.TrialValid;

    return html`
        <gz-trial-product-item
                productTitle="${this.productItem.title}"
                description="${this.productItem.description}"
                imageAlignment="${this.productItem.imageAlignment}"
                imageSrc="${this.productItem.imageSrc}"
                hasBackButton="true">
            <gz-tooltip tooltipText=${tooltipText} class="trial-start-btn ${classes}">
                <gz-button
                        @click="${this.handleProductClicked}"
                        id=${this.productItem.button1?.buttonId}
                        ?disabled="${startButtonMustBeDisabled}">
                    ${this.productItem.button1?.buttonText}
                </gz-button>
            </gz-tooltip>
            <gz-button
                    class="view-demo-btn ${classes}"
                    @click="${this.handleProductClicked}"
                    type="outline"
                    id=${this.productItem.button2?.buttonId}>
                ${this.productItem.button2?.buttonText}
            </gz-button>
            <gz-button
                    class="how-to-btn ${classes}"
                    @click="${this.handleProductClicked}"
                    type="outline"
                    id=${this.productItem.button3?.buttonId}>
                ${this.productItem.button3?.buttonText}
            </gz-button>
            <gz-button
                    @click="${this.handleProductClicked}"
                    type="outline"
                    color="negative"
                    id=${this.productItem.button4?.buttonId}
                    class="trial-stop-btn ${classes}">
                ${this.productItem.button4?.buttonText}
            </gz-button>
        </gz-trial-product-item>
    `;
  }

  private handleButtonClicked(event: Event): void {
    if (!event.target) {
      return;
    }

    const clickEvent = new CustomEvent(LandingPageEventsName.MSP_TRIAL_ACTION, {
      detail: {
        target: event.target,
        productId: this.productItem?.type,
      },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(clickEvent);
  }

}
